import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

const COUNTRY_CATALOG_URL = "https://restcountries.com/v3.1/all?fields=cca2,cca3,name";
const COUNTRY_CATALOG_SESSION_KEY = "ngud:country-catalog:session";
const COUNTRY_CATALOG_LOCAL_KEY = "ngud:country-catalog:local";
const COUNTRY_CATALOG_LOCAL_TS = "ngud:country-catalog:local:ts";
const COUNTRY_CATALOG_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const EMPTY_LIST = [];

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

const readCachedList = (key, storage) => {
    try {
        const raw = storage?.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const writeCachedList = (key, data, storage) => {
    try {
        storage?.setItem(key, JSON.stringify(data));
    } catch {
        // ignore storage quota / privacy mode errors
    }
};

const toCountryEntry = (value) => {
    const iso3 = String(value?.cca3 || "").toUpperCase();
    const iso2 = String(value?.cca2 || "").toUpperCase();
    const name = String(value?.name?.common || value?.name?.official || "").trim();
    if (!iso3 || !name) return null;
    return { name, iso2, iso3 };
};

let memoryCatalog = null;
let memoryCatalogPromise = null;

const hydrateFromStorage = () => {
    if (memoryCatalog) return memoryCatalog;

    const fromSession = readCachedList(COUNTRY_CATALOG_SESSION_KEY, globalThis.sessionStorage);
    if (fromSession?.length) {
        memoryCatalog = fromSession;
        return fromSession;
    }

    const localTs = Number(globalThis.localStorage?.getItem(COUNTRY_CATALOG_LOCAL_TS) || 0);
    const localIsFresh = Number.isFinite(localTs) && Date.now() - localTs <= COUNTRY_CATALOG_TTL_MS;
    if (!localIsFresh) return null;

    const fromLocal = readCachedList(COUNTRY_CATALOG_LOCAL_KEY, globalThis.localStorage);
    if (fromLocal?.length) {
        memoryCatalog = fromLocal;
        return fromLocal;
    }

    return null;
};

const fetchCountryCatalog = async () => {
    if (memoryCatalog?.length) return memoryCatalog;

    const hydrated = hydrateFromStorage();
    if (hydrated?.length) return hydrated;

    if (memoryCatalogPromise) return memoryCatalogPromise;

    memoryCatalogPromise = fetch(COUNTRY_CATALOG_URL)
        .then((response) => {
            if (!response.ok) throw new Error(`country catalog fetch failed: ${response.status}`);
            return response.json();
        })
        .then((payload) => {
            const list = (Array.isArray(payload) ? payload : [])
                .map(toCountryEntry)
                .filter(Boolean)
                .sort((a, b) => a.name.localeCompare(b.name));

            if (!list.length) {
                throw new Error("country catalog fetch returned no data");
            }

            memoryCatalog = list;
            writeCachedList(COUNTRY_CATALOG_SESSION_KEY, list, globalThis.sessionStorage);
            writeCachedList(COUNTRY_CATALOG_LOCAL_KEY, list, globalThis.localStorage);
            try {
                globalThis.localStorage?.setItem(COUNTRY_CATALOG_LOCAL_TS, String(Date.now()));
            } catch {
                // ignore storage quota / privacy mode errors
            }
            return list;
        })
        .finally(() => {
            memoryCatalogPromise = null;
        });

    return memoryCatalogPromise;
};

const hasCachedCountries = () => {
    try {
        return !!globalThis.sessionStorage?.getItem(COUNTRY_CATALOG_SESSION_KEY);
    } catch {
        return false;
    }
};

export default function useCountryCatalog(options = {}) {
    const isBrowser = typeof window !== "undefined";

    // If data is already in memory or session cache, enable immediately.
    // Otherwise defer to idle time so the cold-start network request doesn't
    // compete with the critical bootstrap queries.
    const [idleReady, setIdleReady] = useState(
        () => !isBrowser || !!memoryCatalog || hasCachedCountries()
    );

    useEffect(() => {
        if (idleReady || !isBrowser) return undefined;

        if (globalThis.requestIdleCallback) {
            const id = globalThis.requestIdleCallback(() => setIdleReady(true), { timeout: 3000 });
            return () => globalThis.cancelIdleCallback(id);
        }

        const id = setTimeout(() => setIdleReady(true), 2000);
        return () => clearTimeout(id);
    }, [idleReady, isBrowser]);

    const isEnabled = (options?.enabled ?? isBrowser) && idleReady;
    const query = useQuery({
        queryKey: ["country-catalog"],
        queryFn: fetchCountryCatalog,
        staleTime: Infinity,
        gcTime: 24 * 60 * 60 * 1000,
        enabled: isEnabled,
        // A CORS/network failure against the third-party API won't resolve by
        // retrying, so avoid react-query's default 3x retry noise.
        retry: false,
    });

    const countries = query.data ?? EMPTY_LIST;

    const byIso3 = useMemo(() => {
        const map = new Map();
        countries.forEach((country) => {
            if (!country?.iso3) return;
            map.set(String(country.iso3).toUpperCase(), country);
        });
        return map;
    }, [countries]);

    const byIso2 = useMemo(() => {
        const map = new Map();
        countries.forEach((country) => {
            if (!country?.iso2) return;
            map.set(String(country.iso2).toUpperCase(), country);
        });
        return map;
    }, [countries]);

    const byName = useMemo(() => {
        const map = new Map();
        countries.forEach((country) => {
            if (!country?.name) return;
            map.set(normalizeText(country.name), country);
        });
        return map;
    }, [countries]);

    const findCountry = useCallback(({ iso3, iso2, name } = {}) => {
        if (iso3) {
            const matchedByIso3 = byIso3.get(String(iso3).toUpperCase());
            if (matchedByIso3) return matchedByIso3;
        }
        if (iso2) {
            const matchedByIso2 = byIso2.get(String(iso2).toUpperCase());
            if (matchedByIso2) return matchedByIso2;
        }
        if (name) {
            return byName.get(normalizeText(name)) || null;
        }
        return null;
    }, [byIso2, byIso3, byName]);

    const resolveISO3 = useCallback((rawIsoCode, fallbackName) => {
        const raw = String(rawIsoCode ?? "").trim();
        if (!raw && !fallbackName) return "";

        const upper = raw ? raw.toUpperCase() : "";
        if (upper) {
            const matched = byIso3.get(upper) || byIso2.get(upper);
            if (matched?.iso3) return String(matched.iso3).toUpperCase();
            return upper;
        }

        const byCountryName = fallbackName ? byName.get(normalizeText(fallbackName)) : null;
        return byCountryName?.iso3 ? String(byCountryName.iso3).toUpperCase() : "";
    }, [byIso2, byIso3, byName]);

    return {
        countries,
        byIso2,
        byIso3,
        findCountry,
        resolveISO3,
        isFetching: query.isFetching,
    };
}
